import React from 'react';
import { Modal, Button, Radio, message } from 'antd';
import withStyles from 'react-jss';
import { StylesProps } from 'src/library/types';
import { ExclamationCircleFilled } from '@ant-design/icons';
import { RejectVehicleRequestStyles } from './RejectVehicleRequestModal.styles';
import { rejectVehicleRequest, rejectVehicleRequestIST } from './AssignVehicle.api';
import { useTranslation } from 'react-i18next';

interface IProps extends StylesProps<ReturnType<typeof RejectVehicleRequestStyles>> {
  isVisible: boolean;
  handleClose: (onSuccess?: boolean) => void;
  vehicleData;
  rejectionReasonList: Array<string>;
  tripType: string;
}

const RejectVehicleRequestModal = (props: IProps): React.ReactElement => {
  const { isVisible, handleClose, vehicleData, classes, rejectionReasonList, tripType } = props;

  const [rejectionReason, setRejectionReason] = React.useState('');
  const [isLoading, setIsLoading] = React.useState(false);
  const { t } = useTranslation();

  const handleOk = async () : Promise<void> => {
    try {
      if (!rejectionReason) {
        message.warning(t('Please select a reason'));
      } else {
        setIsLoading(true);
        const body = {
          rejectionReason,
          vendor_id: vehicleData?.vendor_id,
        };
        if (tripType === 'ist') {
          body['istId'] = vehicleData?.id;
        } else {
          body['tripId'] = vehicleData?.id;
        }
        const response = tripType === 'ist' ? await rejectVehicleRequestIST(body) : await rejectVehicleRequest(body);
        if (response.isSuccess) {
          handleClose(true);
          message.success(t('Indent rejected successfully'));
        } else {
          message.error(response.errorMessage);
        }
        setIsLoading(false);
      }
    } catch (err) {
      message.error(err.message);
      setIsLoading(false);
    }
  };

  const renderWarning = () => {
    return (
      <div className={classes.rejectionWarning}>
        <ExclamationCircleFilled style={{ color: '#ff0000' }} /> &nbsp;
        {t('Attention! You will not be allowed to accept the indent once you reject it')}
      </div>
    );
  };

  const onChangeRejectionReason = (e) => {
    setRejectionReason(e.target.value);
  };

  const renderReason = () => {
    return (
      <div>
        <h4>{t('Select a reason')}</h4>
        <Radio.Group
          value={rejectionReason}
          onChange={onChangeRejectionReason}
          options={rejectionReasonList}
          className={classes.reasonAlignment}
        />
      </div>
    );
  };

  return (
    <Modal
      visible={isVisible}
      title={t("Reject Indent?")}
      onCancel={() => handleClose()}
      className={classes.modal}
      footer={(
        <div>
          <Button
            onClick={() => handleClose()}
            size="large"
            style={{ border: '1px solid rgba(66, 66, 66, 0.5)', backgroundColor: '#FFFFFF', color: '#424242' }}
            className={classes.button}
          >
            {t('Cancel')}
          </Button>
          <Button
            onClick={handleOk}
            size="large"
            style={{ border: '1px solid #0780FE', backgroundColor: '#FF0000', color: '#FFFFFF' }}
            className={classes.button}
            loading={isLoading}
          >
            {t('Reject')}
          </Button>
        </div>
      )}
    >
      <div>
        {renderWarning()}
        {renderReason()}
      </div>
    </Modal>
  );
};

export default withStyles(RejectVehicleRequestStyles)(RejectVehicleRequestModal);
